import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Mail,
  Calendar as CalendarIcon,
  FolderOpen,
  ExternalLink,
  ArrowDownLeft,
  ArrowUpRight,
  Video,
  Plus,
  FileText,
  FileSpreadsheet,
  Presentation,
  File,
  CheckCircle2,
  Link2,
  Sparkles,
} from "lucide-react";

const timeAgo = (iso) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const diff = (Date.now() - d.getTime()) / 86400000;
  if (diff < 1) return "today";
  if (diff < 2) return "yesterday";
  return `${Math.floor(diff)}d ago`;
};

const fmtDateTime = (iso) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const fileIcons = {
  doc: FileText,
  sheet: FileSpreadsheet,
  slides: Presentation,
  pdf: File,
};

const DemoBadge = () => (
  <span className="rounded-full text-[10px] uppercase tracking-wider px-2 py-0.5 bg-amber-100 text-amber-700 font-[Manrope] font-medium" data-testid="demo-badge">
    Demo data
  </span>
);

const LiveBadge = () => (
  <span className="rounded-full text-[10px] uppercase tracking-wider px-2 py-0.5 bg-emerald-100 text-emerald-700 font-[Manrope] font-medium inline-flex items-center gap-1" data-testid="live-badge">
    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Live
  </span>
);

const SectionCard = ({ icon: Icon, title, action, children, testid, live }) => (
  <div className="border border-border bg-white rounded-md" data-testid={testid}>
    <div className="flex items-center justify-between px-5 py-4 border-b border-border">
      <div className="flex items-center gap-2.5">
        <Icon className="h-4 w-4 text-[#193962]" />
        <h3 className="font-[Outfit] font-semibold tracking-tight">{title}</h3>
        {live ? <LiveBadge /> : <DemoBadge />}
      </div>
      {action}
    </div>
    {children}
  </div>
);

function GmailSection({ clientId, reloadKey }) {
  const [data, setData] = useState({ threads: [], mock: true });
  useEffect(() => {
    api.get(`/clients/${clientId}/workspace/gmail`).then((r) => setData(r.data)).catch(() => {});
  }, [clientId, reloadKey]);

  return (
    <SectionCard icon={Mail} title="Email Threads" testid="gmail-section" live={data.mock === false}>
      <div className="divide-y divide-border">
        {(data.threads || []).length === 0 && (
          <p className="px-5 py-8 text-center text-sm text-muted-foreground font-[Manrope]">No email threads found.</p>
        )}
        {(data.threads || []).map((t) => (
          <div key={t.id} className="px-5 py-3.5 flex items-start gap-3 hover:bg-zinc-50 transition-colors" data-testid={`gmail-thread-${t.id}`}>
            <div className={`mt-1 h-7 w-7 rounded-full flex items-center justify-center shrink-0 ${t.direction === "inbound" ? "bg-blue-50 text-blue-600" : "bg-emerald-50 text-emerald-600"}`}>
              {t.direction === "inbound" ? <ArrowDownLeft className="h-3.5 w-3.5" /> : <ArrowUpRight className="h-3.5 w-3.5" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className={`font-[Manrope] text-sm truncate ${t.unread ? "font-semibold" : "font-medium"}`}>{t.subject}</p>
                {t.unread && <span className="h-1.5 w-1.5 rounded-full bg-[#193962] shrink-0" />}
              </div>
              <p className="text-sm text-muted-foreground font-[Manrope] truncate">{t.snippet}</p>
              <p className="text-xs text-zinc-400 font-[Manrope] mt-0.5">
                {t.direction === "inbound" ? `From ${t.from_name}` : `To ${t.to_email}`} · {t.message_count} message{t.message_count > 1 ? "s" : ""}
              </p>
            </div>
            <span className="text-xs text-zinc-400 font-[Manrope] shrink-0">{timeAgo(t.date)}</span>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

function CalendarSection({ clientId, clientName, reloadKey }) {
  const [data, setData] = useState({ events: [], mock: true });
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ summary: `Meeting with ${clientName}`, date: "", time: "10:00", duration: "60" });

  const load = () => api.get(`/clients/${clientId}/workspace/calendar`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId, reloadKey]);

  const schedule = async () => {
    if (!form.date) { toast.error("Pick a date"); return; }
    setCreating(true);
    try {
      const start = new Date(`${form.date}T${form.time}:00`);
      const end = new Date(start.getTime() + parseInt(form.duration) * 60000);
      const res = await api.post(`/clients/${clientId}/workspace/calendar/draft`, {
        summary: form.summary,
        description: `Meeting scheduled from Toren PATH CRM.`,
        start: start.toISOString(),
        end: end.toISOString(),
      });
      window.open(res.data.calendar_url, "_blank");
      toast.success("Opening Google Calendar draft…");
      setOpen(false);
    } catch (e) {
      toast.error("Failed to create draft");
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <SectionCard
        icon={CalendarIcon}
        title="Upcoming Meetings"
        testid="calendar-section"
        live={data.mock === false}
        action={
          <Button size="sm" onClick={() => setOpen(true)} data-testid="schedule-meeting-btn" className="bg-[#193962] hover:bg-[#12294A] text-white transition-all duration-200 hover:-translate-y-[1px]">
            <Plus className="h-4 w-4 mr-1.5" /> Schedule Meeting
          </Button>
        }
      >
        <div className="divide-y divide-border">
          {(data.events || []).length === 0 && (
            <p className="px-5 py-8 text-center text-sm text-muted-foreground font-[Manrope]">No upcoming meetings.</p>
          )}
          {(data.events || []).map((e) => (
            <div key={e.id} className="px-5 py-3.5 flex items-center gap-4 hover:bg-zinc-50 transition-colors" data-testid={`calendar-event-${e.id}`}>
              <div className="flex flex-col items-center justify-center h-12 w-12 rounded-md bg-[#193962]/5 text-[#193962] shrink-0">
                <span className="text-[10px] uppercase font-[Manrope] font-semibold">{new Date(e.start).toLocaleString(undefined, { month: "short" })}</span>
                <span className="font-[Outfit] font-bold text-lg leading-none">{new Date(e.start).getDate()}</span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-[Manrope] font-medium text-sm truncate">{e.summary}</p>
                <p className="text-xs text-muted-foreground font-[Manrope]">{fmtDateTime(e.start)} · {e.location}</p>
              </div>
              {e.meet_link && (
                <a href={e.meet_link} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-[#193962] font-[Manrope] hover:underline shrink-0">
                  <Video className="h-3.5 w-3.5" /> Open
                </a>
              )}
            </div>
          ))}
        </div>
      </SectionCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Outfit] text-2xl tracking-tight">Schedule Meeting</DialogTitle>
            <DialogDescription className="font-[Manrope]">Opens a prefilled Google Calendar event draft in a new tab.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="font-[Manrope] text-sm">Title</Label>
              <Input data-testid="meeting-title-input" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="mt-1.5" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="font-[Manrope] text-sm">Date</Label>
                <Input type="date" data-testid="meeting-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label className="font-[Manrope] text-sm">Time</Label>
                <Input type="time" data-testid="meeting-time-input" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label className="font-[Manrope] text-sm">Mins</Label>
                <Input type="number" data-testid="meeting-duration-input" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="mt-1.5" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={schedule} disabled={creating} data-testid="create-draft-btn" className="bg-[#193962] hover:bg-[#12294A] text-white">
              {creating ? "Opening…" : "Open Calendar Draft"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function DriveSection({ clientId, reloadKey }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/clients/${clientId}/workspace/drive`).then((r) => setData(r.data)).catch(() => {});
  }, [clientId, reloadKey]);

  return (
    <SectionCard
      icon={FolderOpen}
      title="Client Documents"
      testid="drive-section"
      live={data?.mock === false}
      action={
        data && (
          <a href={data.folder_url} target="_blank" rel="noreferrer" data-testid="open-drive-folder" className="flex items-center gap-1.5 text-xs text-[#193962] font-[Manrope] hover:underline">
            Open folder <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )
      }
    >
      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {(data?.files || []).length === 0 && (
          <p className="col-span-full px-1 py-6 text-center text-sm text-muted-foreground font-[Manrope]">No documents found.</p>
        )}
        {(data?.files || []).map((f) => {
          const Icon = fileIcons[f.type] || File;
          return (
            <a
              key={f.id}
              href={f.web_view_link}
              target="_blank"
              rel="noreferrer"
              data-testid={`drive-file-${f.id}`}
              className="flex items-center gap-3 border border-border rounded-md px-3 py-2.5 hover:border-[#193962]/40 hover:bg-zinc-50 transition-colors"
            >
              <Icon className="h-5 w-5 text-[#193962] shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="font-[Manrope] text-sm truncate">{f.name}</p>
                <p className="text-xs text-zinc-400 font-[Manrope]">Modified {timeAgo(f.modified)}</p>
              </div>
              {f.ai_generated && <Sparkles className="h-3.5 w-3.5 text-[#C99734] shrink-0" title="AI-generated" />}
            </a>
          );
        })}
      </div>
    </SectionCard>
  );
}

function GoogleConnectBanner({ status, onConnect, onDisconnect, connecting }) {
  if (!status.configured) {
    return (
      <div className="border border-amber-200 bg-amber-50 rounded-md px-4 py-3 flex items-center gap-2.5" data-testid="google-status-banner">
        <p className="text-sm text-amber-800 font-[Manrope]">
          <strong>Demo mode:</strong> Gmail, Calendar and Drive show sample data. Add Google Workspace credentials to go live. "Schedule Meeting" opens a real Google Calendar draft.
        </p>
      </div>
    );
  }
  if (status.connected) {
    return (
      <div className="border border-emerald-200 bg-emerald-50 rounded-md px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="google-status-banner">
        <p className="text-sm text-emerald-800 font-[Manrope] flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4" />
          <span><strong>Google Workspace connected</strong>{status.email ? ` as ${status.email}` : ""}. Live Gmail, Calendar & Drive.</span>
        </p>
        <Button size="sm" variant="outline" onClick={onDisconnect} data-testid="google-disconnect-btn" className="border-emerald-300 text-emerald-800 hover:bg-emerald-100">
          Disconnect
        </Button>
      </div>
    );
  }
  return (
    <div className="border border-[#193962]/20 bg-[#193962]/5 rounded-md px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="google-status-banner">
      <p className="text-sm text-[#193962] font-[Manrope]">
        <strong>Connect Google Workspace</strong> to replace demo data with your real Gmail threads, Calendar events and Drive files for this client.
      </p>
      <Button size="sm" onClick={onConnect} disabled={connecting} data-testid="google-connect-btn" className="bg-[#193962] hover:bg-[#12294A] text-white">
        <Link2 className="h-4 w-4 mr-1.5" /> {connecting ? "Opening…" : "Connect Google"}
      </Button>
    </div>
  );
}

export default function WorkspaceTab({ client }) {
  const [status, setStatus] = useState({ configured: false, connected: false });
  const [reloadKey, setReloadKey] = useState(0);
  const [connecting, setConnecting] = useState(false);

  const loadStatus = useCallback(() => {
    api.get("/google/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const connect = async () => {
    setConnecting(true);
    try {
      const r = await api.get("/google/connect");
      window.open(r.data.authorization_url, "google-connect", "width=520,height=680");
      const onMsg = (e) => {
        if (e.data === "google-connected") {
          toast.success("Google Workspace połączony");
          loadStatus();
          setReloadKey((k) => k + 1);
          window.removeEventListener("message", onMsg);
        } else if (e.data === "google-error") {
          toast.error("Połączenie z Google nie powiodło się");
          window.removeEventListener("message", onMsg);
        }
      };
      window.addEventListener("message", onMsg);
    } catch (e) {
      toast.error("Nie udało się rozpocząć połączenia");
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    try {
      await api.delete("/google/disconnect");
      toast.success("Rozłączono Google Workspace");
      loadStatus();
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error("Nie udało się rozłączyć");
    }
  };

  return (
    <div className="space-y-6" data-testid="workspace-tab">
      <GoogleConnectBanner status={status} onConnect={connect} onDisconnect={disconnect} connecting={connecting} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <GmailSection clientId={client.client_id} reloadKey={reloadKey} />
        <CalendarSection clientId={client.client_id} clientName={client.company_name} reloadKey={reloadKey} />
      </div>
      <DriveSection clientId={client.client_id} reloadKey={reloadKey} />
    </div>
  );
}
